/**
 * Generated login names, per docs/PROFILE.md.
 *
 * A child gets one name made of three harmless words -- TanzendesBlauesPferd --
 * which is both the display name and the login. There is no nickname field and
 * no handle field, so there is nothing to type a real first name into.
 *
 * Pure functions, no I/O: the client rolls the name and derives the PIN key
 * from the handle before anything reaches the worker.
 *
 * Curation rules for the word lists (docs/PROFILE.md §3), applied deliberately:
 *
 * - No umlauts and no sharp s anywhere. A name a child cannot type on the
 *   keyboard in front of them is a name they cannot log in with, and folding
 *   "ue" and "u" onto one key would make two different names collide. This
 *   costs us Gruen, Loewe and Baer; that is the cheaper loss.
 * - Every adjective takes -er / -e / -es without a stem change, so the three
 *   genders stay grammatical. That rules out dunkel (dunkler) and hoch (hoher).
 * - Nothing that works as a playground insult, in either half. No Esel, Kuh,
 *   Gans, Ziege, Schwein, Ratte; nothing about size, weight or laziness. A
 *   name is assigned, not chosen, so the child cannot walk away from a
 *   combination that gets them teased.
 */

import { deaccent } from "./text.js";

export type Gender = "m" | "f" | "n";

/** Strong nominative endings. The whole inflection problem is this table. */
const ENDING: Record<Gender, string> = { m: "er", f: "e", n: "es" };

/** Stems without an ending: "Tanzend" -> Tanzender / Tanzende / Tanzendes. */
export const ADJECTIVES: readonly string[] = [
  "Tanzend",
  "Lachend",
  "Singend",
  "Springend",
  "Fliegend",
  "Schlafend",
  "Wandernd",
  "Kletternd",
  "Staunend",
  "Froh",
  "Klug",
  "Flink",
  "Mutig",
  "Ruhig",
  "Freundlich",
  "Fleissig",
  "Neugierig",
  "Sanft",
  "Stark",
  "Schnell",
  "Leis",
  "Geduldig",
  "Ehrlich",
  "Tapfer",
  "Wachsam",
  "Artig",
  "Brav",
  "Nett",
  "Klar",
  "Warm",
  "Rund",
  "Weich",
  "Leicht",
  "Frisch",
  "Lustig",
  "Witzig",
  "Fein",
  "Wach",
  "Emsig",
  "Schlau",
  "Treu",
  "Heiter",
  "Munter",
  "Weis",
] as const;

export const COLOURS: readonly string[] = [
  "Blau",
  "Rot",
  "Gelb",
  "Weiss",
  "Schwarz",
  "Braun",
  "Grau",
  "Silbern",
  "Golden",
  "Bunt",
  "Violett",
  "Hellblau",
  "Hellgrau",
  "Dunkelrot",
  "Dunkelblau",
] as const;

export interface Animal {
  readonly word: string;
  readonly gender: Gender;
}

export const ANIMALS: readonly Animal[] = [
  { word: "Fuchs", gender: "m" },
  { word: "Hund", gender: "m" },
  { word: "Dachs", gender: "m" },
  { word: "Biber", gender: "m" },
  { word: "Otter", gender: "m" },
  { word: "Adler", gender: "m" },
  { word: "Falke", gender: "m" },
  { word: "Rabe", gender: "m" },
  { word: "Hase", gender: "m" },
  { word: "Igel", gender: "m" },
  { word: "Delfin", gender: "m" },
  { word: "Tiger", gender: "m" },
  { word: "Panda", gender: "m" },
  { word: "Storch", gender: "m" },
  { word: "Elch", gender: "m" },
  { word: "Kater", gender: "m" },
  { word: "Pinguin", gender: "m" },
  { word: "Marder", gender: "m" },
  { word: "Wolf", gender: "m" },
  { word: "Luchs", gender: "m" },
  { word: "Papagei", gender: "m" },
  { word: "Frosch", gender: "m" },
  { word: "Schwan", gender: "m" },
  { word: "Reiher", gender: "m" },
  { word: "Specht", gender: "m" },
  { word: "Salamander", gender: "m" },
  { word: "Seestern", gender: "m" },
  { word: "Maus", gender: "f" },
  { word: "Katze", gender: "f" },
  { word: "Robbe", gender: "f" },
  { word: "Ente", gender: "f" },
  { word: "Biene", gender: "f" },
  { word: "Ameise", gender: "f" },
  { word: "Amsel", gender: "f" },
  { word: "Eule", gender: "f" },
  { word: "Taube", gender: "f" },
  { word: "Schnecke", gender: "f" },
  { word: "Giraffe", gender: "f" },
  { word: "Libelle", gender: "f" },
  { word: "Meise", gender: "f" },
  { word: "Forelle", gender: "f" },
  { word: "Grille", gender: "f" },
  { word: "Muschel", gender: "f" },
  { word: "Antilope", gender: "f" },
  { word: "Schwalbe", gender: "f" },
  { word: "Pferd", gender: "n" },
  { word: "Schaf", gender: "n" },
  { word: "Reh", gender: "n" },
  { word: "Kamel", gender: "n" },
  { word: "Lama", gender: "n" },
  { word: "Zebra", gender: "n" },
  { word: "Pony", gender: "n" },
  { word: "Kaninchen", gender: "n" },
  { word: "Nashorn", gender: "n" },
  { word: "Wiesel", gender: "n" },
  { word: "Rentier", gender: "n" },
  { word: "Fohlen", gender: "n" },
  { word: "Ferkel", gender: "n" },
  { word: "Krokodil", gender: "n" },
  { word: "Murmeltier", gender: "n" },
] as const;

/**
 * The key a name is stored and looked up under. Everything a child could
 * plausibly type -- shouting it, spacing the three words out, adding a
 * hyphen -- has to land on the same string.
 */
export function loginKey(input: string): string {
  return deaccent(input).replace(/[^a-z0-9]/g, "");
}

export interface RolledName {
  /** Readable compound with inner capitals: TanzendesBlauesPferd. */
  display: string;
  /** loginKey(display), what users.handle stores. */
  handle: string;
  /** The same three words separately, for a display that wants to break them. */
  parts: [string, string, string];
}

export function buildName(adjective: string, colour: string, animal: Animal): RolledName {
  const ending = ENDING[animal.gender];
  const parts: [string, string, string] = [
    `${adjective}${ending}`,
    `${colour}${ending}`,
    animal.word,
  ];
  const display = parts.join("");
  return { display, handle: loginKey(display), parts };
}

function pick<T>(items: readonly T[], random: () => number): T {
  return items[Math.floor(random() * items.length)] as T;
}

/** One name. Roll again for a different one -- it is free and has no state. */
export function rollName(random: () => number = Math.random): RolledName {
  return buildName(pick(ADJECTIVES, random), pick(COLOURS, random), pick(ANIMALS, random));
}

/**
 * True when a display name could have come out of rollName. Used by tests and
 * available to the worker if it ever wants to insist on generated names; the
 * API stays lenient so the existing short handles keep working.
 */
export function isRolledName(display: string): boolean {
  for (const animal of ANIMALS) {
    if (!display.endsWith(animal.word)) continue;
    const head = display.slice(0, -animal.word.length);
    const ending = ENDING[animal.gender];
    for (const adjective of ADJECTIVES) {
      const prefix = `${adjective}${ending}`;
      if (!head.startsWith(prefix)) continue;
      const rest = head.slice(prefix.length);
      if (COLOURS.some((colour) => `${colour}${ending}` === rest)) return true;
    }
  }
  return false;
}

/** Every name the lists can produce. Only tests need this. */
export function allNames(): RolledName[] {
  const out: RolledName[] = [];
  for (const adjective of ADJECTIVES) {
    for (const colour of COLOURS) {
      for (const animal of ANIMALS) out.push(buildName(adjective, colour, animal));
    }
  }
  return out;
}
